import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles} from '@mantine/core';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import EditableComponent from 'notebook/mediocre/core-ui/EditableComponent';
import {SelectionPartition} from 'notebook/mediocre/EditorTypes';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {BenchlingSelection, SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {acceptsGraphemeEdit} from 'notebook/util/GraphemeLimit';
import GraphemeLimitBoundary from 'notebook/util/GraphemeLimitBoundary';
import notifications from 'notifications';
import {ElementClipboardEvent, ElementKeyboardEvent} from 'util/Types';

/** Placeholder shown for an empty, editable caption while hovered. */
export const GRID_ITEM_CAPTION_PLACEHOLDER = 'Click to add caption';

export interface GridItemCaptionUpdate {
  text: string;
  formats: Format;
}

/** Shared toast id, so repeated limit warnings update the open toast instead of adding new ones. */
const CAPTION_LIMIT_WARNING_ID = 'grid-item-caption-limit-warning';

function onRejectCaptionLimit({maxGraphemes}: {maxGraphemes: number}): void {
  notifications.warn(`Maximum number of characters allowed within the caption is ${maxGraphemes}`, {
    id: CAPTION_LIMIT_WARNING_ID,
  });
}

function closeCaptionLimitWarning(): void {
  notifications.close(CAPTION_LIMIT_WARNING_ID);
}

export interface GridItemCaptionProps {
  /** The cid of the contained note part this caption belongs to. */
  cid: string;
  /** Current caption text. */
  text: string;
  /** Current caption rich-text formats, if any. */
  formats: Format | undefined;
  /** Whether the note is locked (read-only). */
  locked?: boolean;
  /** Whether we are in view-only mode. Right now, every place which has locked = true also has viewer = true, so a bit redundant, but adding for comprehensiveness */
  viewer?: boolean;
  /** Whether the entry is fill-in only, so its captions can't be edited even though it isn't locked. */
  isProtected?: boolean;
  /** Whether we're rendering the print/export/read-only viewer. */
  isPrintPreviewView?: boolean;
  /** Whether spell check should be enabled on the editable. */
  spellCheck?: boolean;
  /** Persist an edited caption back to the grid. */
  onUpdateCaption: (update: GridItemCaptionUpdate) => void;
  /**
   * Restore the mediocre selection to a cid after an edit (from
   * editorProps.requestSelection). Without it, the note-update re-render resets
   * the caret to offset 0 — text would insert at the start on every keystroke.
   */
  requestSelection?: (args: {selection: BenchlingSelection}) => void;
  /** Records an undo point before each edit (from editorProps.storeState). */
  storeState?: (args: {selection: BenchlingSelection}) => void;
  /**
   * Discards the undo point recorded by storeState when an edit is rejected after it was recorded
   * (from editorProps.undoStoreState).
   */
  undoStoreState?: () => void;
  /** Handles undo/redo keystrokes against the note history (from editorProps.restoreState). */
  restoreState?: (
    e: ElementKeyboardEvent,
    selectionRestoreData: {selection: SelectionRestoreData}
  ) => boolean;
  /**
   * Called on mousedown, before the browser places the caret, to clear the editor's block selection
   * (and the grid's item selection).
   */
  onBeginEdit?: () => void;
}

// Not applied in print preview or PDF export, which use the notePartGrid-caption rule in mediocre.less.
const useStyles = createStyles((theme) => ({
  caption: {
    color: theme.colors.gray[8],
    fontSize: theme.fontSizes.md,
    lineHeight: 1.4,
    marginTop: theme.spacing.xs,
    padding: `0 ${theme.spacing.xs}`,
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // Only applied when the caption is editable.
  editable: {
    position: 'relative',
    border: '1px solid transparent',
    borderRadius: theme.radius.sm,
    '&:focus-within': {
      borderColor: theme.colors.gray[4],
    },
  },
  // Overlays the editable rather than using `.editable:empty`, since multiline editables always contain a
  // trailing newline and never match `:empty`. The text is read from data-placeholder so it is present in the DOM.
  emptyPlaceholder: {
    '&:hover::before': {
      content: 'attr(data-placeholder)',
      position: 'absolute',
      inset: 0,
      padding: `0 ${theme.spacing.xs}`,
      color: theme.colors.gray[4],
      pointerEvents: 'none',
    },
  },
}));

/**
 * Grid-native, click-to-edit rich-text caption rendered beneath a grid image.
 *
 * A lightweight caption-only editable (unlike CaptionEditable, which owns the full caption-plus-component
 * layout for top-level note parts). It wraps mediocre's EditableComponent, and enforces a maximum grapheme
 * count of BenchlingConfig.ELN_IMAGE_GRID_CAPTION_LIMIT (defaults to 500), with a warning box + toast when
 * you are approaching or at the limit.
 *
 * In read-only mode (document locked, print/export, or protected), the caption is not editable and
 * has no interactivity.
 *
 * In print preview and PDF export, the caption is styled by mediocre.less instead of useStyles.
 */
export default function GridItemCaptionEditable({
  cid,
  text,
  formats,
  locked,
  viewer,
  isProtected,
  isPrintPreviewView,
  spellCheck,
  onUpdateCaption,
  requestSelection,
  storeState,
  undoStoreState,
  restoreState,
  onBeginEdit,
}: GridItemCaptionProps): JSX.Element | null {
  const isReadOnly =
    Boolean(locked) || Boolean(isPrintPreviewView) || Boolean(isProtected) || Boolean(viewer);

  const handleUpdate = useCallback(
    ({
      text: nextText,
      formats: nextFormats,
      selection: nextSelection,
    }: {
      text: string;
      formats: Format;
      selection?: BenchlingSelection;
    }) => {
      // Rejected edits never reach the update handler
      closeCaptionLimitWarning();

      onUpdateCaption({text: nextText, formats: nextFormats});

      // The note-update re-render resets the caret to offset 0, so restore the
      // selection.
      const selection = nextSelection ?? selutil.getSelectionIndices();
      if (selection != null) {
        requestSelection?.({selection});
      }
    },
    [onUpdateCaption, requestSelection]
  );

  const handlePaste = useCallback(
    (
      e: SelectionPartition & {
        originalEvent: ElementClipboardEvent;
      }
    ) => {
      const {before, selected, after, originalEvent} = e;

      const parts = wyciwyp.processClipboard(originalEvent.clipboardData, {});
      const pasteText = parts.map((part) => part.text).join(' ');

      const next = formatters.replaceText(
        formats ?? formatters.defaultFormats(),
        {before, selected, after},
        pasteText
      );

      const maxGraphemes = BenchlingConfig.ELN_IMAGE_GRID_CAPTION_LIMIT;

      if (!acceptsGraphemeEdit(text, next.text, maxGraphemes)) {
        // EditableComponent records an undo point before calling a custom paste handler.
        undoStoreState?.();
        onRejectCaptionLimit({maxGraphemes});
        return;
      }

      closeCaptionLimitWarning();
      onUpdateCaption({text: next.text, formats: next.formats});
      const selectionIndices = formatters.getSelectionIndicesFromFormatIndices(
        next.formats,
        next.start,
        next.end
      );
      requestSelection?.({selection: {cid, start: selectionIndices.end, end: selectionIndices.end}});
    },
    [onUpdateCaption, requestSelection, undoStoreState, cid, formats, text]
  );

  // EditableComponent always prevents the default Enter and defers to splitHandler,
  // so Enter (with or without Shift) inserts the newline here, replacing any selected text.
  const handleSplit = useCallback(
    ({before, selected, after}: {before: string; selected: string; after: string}) => {
      const next = formatters.replaceText(
        formats ?? formatters.defaultFormats(),
        {before, selected, after},
        '\n'
      );
      const maxGraphemes = BenchlingConfig.ELN_IMAGE_GRID_CAPTION_LIMIT;

      if (!acceptsGraphemeEdit(text, next.text, maxGraphemes)) {
        // EditableComponent records an undo point before calling splitHandler.
        undoStoreState?.();
        onRejectCaptionLimit({maxGraphemes});
        return;
      }
      closeCaptionLimitWarning();
      onUpdateCaption({text: next.text, formats: next.formats});
      const selectionIndices = formatters.getSelectionIndicesFromFormatIndices(
        next.formats,
        next.start,
        next.end
      );
      requestSelection?.({selection: {cid, start: selectionIndices.end, end: selectionIndices.end}});
    },
    [cid, formats, onUpdateCaption, requestSelection, undoStoreState, text]
  );

  // Keep caption clicks away from GridItemWrapper (which prevents the default mousedown and selects
  // the image) and from the note editor.
  const handleCaptionMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onBeginEdit?.();
    },
    [onBeginEdit]
  );

  // DraggableItem selects the image on click, which would move focus to its hidden focus editable.
  const handleCaptionClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const {classes, cx} = useStyles();
  const showPlaceholder = !isReadOnly && !text;

  // If we are in read-only mode and there is no caption text to display, return null rather than rendering
  // an empty EditableComponent which still takes up space.
  if (isReadOnly && !text) {
    return null;
  }

  return (
    <Box
      // Print preview and PDF export are styled by the notePartGrid-caption rule in mediocre.less.
      className={cx(
        isPrintPreviewView ? 'notePartGrid-caption' : classes.caption,
        !isReadOnly && classes.editable,
        showPlaceholder && classes.emptyPlaceholder
      )}
      data-placeholder={showPlaceholder ? GRID_ITEM_CAPTION_PLACEHOLDER : undefined}
      // Read-only captions have no interactivity, so their clicks fall through and select the image.
      onMouseDown={isReadOnly ? undefined : handleCaptionMouseDown}
      onClick={isReadOnly ? undefined : handleCaptionClick}
      {...getTestProps({
        component: 'GridItemCaptionEditable',
        element: isReadOnly ? 'readOnly' : 'editable',
      })}
    >
      <GraphemeLimitBoundary
        onReject={onRejectCaptionLimit}
        maxGraphemes={BenchlingConfig.ELN_IMAGE_GRID_CAPTION_LIMIT}
      >
        <EditableComponent
          // setting index as undefined prevents us from setting data-index on the contentEditable div, which
          // disables the editor toolbar.
          index={undefined}
          cid={cid}
          text={text}
          formats={formats ?? null}
          allowRichText={true}
          allowMultiline={true}
          allowAtSearch={false}
          allowSlashSearch={false}
          spellCheck={Boolean(spellCheck)}
          locked={isReadOnly}
          updateHandler={handleUpdate}
          splitHandler={handleSplit}
          storeState={storeState}
          restoreState={restoreState}
          pasteHandler={handlePaste}
        />
      </GraphemeLimitBoundary>
    </Box>
  );
}
